import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  ChevronRight,
  Home,
  DollarSign,
  Users,
  Building,
  MapPin,
  Target,
  TrendingUp,
  Activity,
  Calendar,
  CheckCircle,
  Clock,
  FileText,
  Phone,
  Mail,
  Video,
  UserCheck,
  Monitor,
  MapPin as LocationIcon,
  Users as TeamIcon
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import { Breadcrumb } from '../../components/Common'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { demoSchedulingService } from '../../services/demoSchedulingService'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const DemoScheduling = () => {
  const navigate = useNavigate()
  const [selectedRows, setSelectedRows] = useState([])
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalDemos: 0,
    scheduledDemos: 0,
    confirmedDemos: 0,
    completedDemos: 0,
    cancelledDemos: 0,
    onlineDemos: 0,
    inPersonDemos: 0,
    totalDuration: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Define columns for the data table
  const columns = [
    {
      key: 'demoCode',
      label: 'Demo Code',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <Video size={14} className="text-blue-600" />
          </div>
          <span className="font-medium text-gray-900">{value}</span>
        </div>
      )
    },
    {
      key: 'demoTitle',
      label: 'Demo Title',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="font-medium text-gray-900">{value}</div>
          <div className="text-sm text-gray-500">{row.prospect?.prospectName || 'N/A'}</div>
        </div>
      )
    },
    {
      key: 'demoType',
      label: 'Type',
      sortable: true,
      render: (value) => {
        const typeColors = {
          'Online': 'bg-blue-100 text-blue-800',
          'In-Person': 'bg-green-100 text-green-800',
          'Recorded': 'bg-purple-100 text-purple-800',
          'Live Demo': 'bg-orange-100 text-orange-800',
          'POC': 'bg-indigo-100 text-indigo-800',
          'Webinar': 'bg-pink-100 text-pink-800',
          'Workshop': 'bg-teal-100 text-teal-800'
        }
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeColors[value] || 'bg-gray-100 text-gray-800'}`}>
            {value}
          </span>
        )
      }
    },
    {
      key: 'demoStatus',
      label: 'Status',
      sortable: true,
      render: (value) => {
        const statusColors = {
          'Scheduled': 'bg-blue-100 text-blue-800',
          'Confirmed': 'bg-green-100 text-green-800',
          'In Progress': 'bg-yellow-100 text-yellow-800',
          'Completed': 'bg-teal-100 text-teal-800',
          'Cancelled': 'bg-red-100 text-red-800',
          'Rescheduled': 'bg-orange-100 text-orange-800',
          'No Show': 'bg-gray-100 text-gray-800'
        }
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[value] || 'bg-gray-100 text-gray-800'}`}>
            {value}
          </span>
        )
      }
    },
    {
      key: 'demoDate',
      label: 'Date & Time',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="text-sm font-medium text-gray-900">
            {value ? new Date(value).toLocaleDateString() : 'N/A'}
          </div>
          <div className="text-xs text-gray-500">{row.demoTime || 'N/A'}</div>
        </div>
      )
    },
    {
      key: 'demoPresenter',
      label: 'Presenter',
      sortable: true,
      render: (value) => (
        <div className="text-gray-900">{value?.firstName} {value?.lastName}</div>
      )
    },
    {
      key: 'demoDuration',
      label: 'Duration',
      sortable: true,
      render: (value) => (
        <div className="flex items-center space-x-1">
          <Clock size={14} className="text-gray-500" />
          <span className="text-sm text-gray-900">{value || 0} min</span>
        </div>
      )
    }
  ]

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      loadData()
      hasLoadedRef.current = true
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [demosResponse, statsResponse] = await Promise.all([
        demoSchedulingService.getAllDemos(),
        demoSchedulingService.getDemoStats()
      ])

      if (demosResponse.success) {
        setData(demosResponse.data.demos || [])
        
        if (demosResponse.data.demos.length === 0 && !hasShownEmptyToastRef.current) {
          toast.info('No demo scheduling records found. Create your first demo to get started.')
          hasShownEmptyToastRef.current = true
        }
      } else {
        toast.error(demosResponse.message || 'Failed to load demo scheduling records')
      }

      if (statsResponse.success) {
        setStats(statsResponse.data)
      }
    } catch (error) {
      console.error('Error loading data:', error)
      toast.error('Failed to load demo scheduling data')
    } finally {
      setLoading(false)
    }
  }

  const handleAdd = () => {
    navigate('/crm/demo-scheduling/new')
  }

  const handleEdit = (row) => {
    navigate('/crm/demo-scheduling/edit', { 
      state: { demoData: row } 
    })
  }

  const handleView = (row) => {
    navigate('/crm/demo-scheduling/view', { 
      state: { demoData: row } 
    })
  }

  const handleDelete = (row) => {
    setDeleteItem(row)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      const response = await demoSchedulingService.deleteDemo(deleteItem._id)
      if (response.success) {
        toast.success('Demo scheduling record deleted successfully')
        loadData()
        setShowDeleteModal(false)
        setDeleteItem(null)
      } else {
        toast.error(response.message || 'Failed to delete demo scheduling record')
      }
    } catch (error) {
      console.error('Error deleting demo scheduling record:', error)
      toast.error('Failed to delete demo scheduling record')
    }
  }

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) {
      toast.error('Please select demo scheduling records to delete')
      return
    }

    try {
      const response = await demoSchedulingService.bulkDeleteDemos(selectedRows)
      if (response.success) {
        toast.success(response.message)
        setSelectedRows([])
        loadData()
      } else {
        toast.error(response.message || 'Failed to delete demo scheduling records')
      }
    } catch (error) {
      console.error('Error bulk deleting demo scheduling records:', error)
      toast.error('Failed to delete demo scheduling records')
    }
  }

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'CRM',
      path: '/crm',
      onClick: () => navigate('/crm')
    },
    {
      label: 'Transactions',
      path: '/crm',
      onClick: () => navigate('/crm')
    },
    {
      label: 'Demo Scheduling',
      path: '/crm/demo-scheduling',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Demo Scheduling</h1>
          <p>Schedule and manage product demonstrations</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn-primary"
            onClick={handleAdd}
          >
            <Plus size={16} />
            Schedule Demo
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <Video size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+18%</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalDemos || 0}</div>
          <div className="metric-title">Total Demos</div>
          <div className="metric-subtitle">vs last month</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Calendar size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+12%</span>
            </div>
          </div>
          <div className="metric-value">{stats.scheduledDemos || 0}</div>
          <div className="metric-title">Scheduled</div>
          <div className="metric-subtitle">Upcoming demos</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+25%</span>
            </div>
          </div>
          <div className="metric-value">{stats.completedDemos || 0}</div>
          <div className="metric-title">Completed</div>
          <div className="metric-subtitle">This month</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <Clock size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+8%</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalDuration || 0}h</div>
          <div className="metric-title">Total Hours</div>
          <div className="metric-subtitle">Demo time</div>
        </div>
      </div>

      {/* Data Table */}
      <div className="dashboard-content">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          onBulkDelete={handleBulkDelete}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          searchPlaceholder="Search demo scheduling records..."
          emptyMessage="No demo scheduling records found. Create your first demo to get started."
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Demo Scheduling"
        message={`Are you sure you want to delete demo "${deleteItem?.demoTitle}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default DemoScheduling
